"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Spinner,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap"
import {
  FaEye,
  FaCheck,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCreditCard,
} from "react-icons/fa"
import DataTable from "react-data-table-component"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { PrintOrder } from "../BadgeRequest/badge-request"
import { toast } from "react-toastify"
import VerificationGuideModal from "./VerificationGuideModal"

interface Props {
  onViewOrder: (order: PrintOrder) => void
  refresh: boolean
}

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

const OrderVerificationList: React.FC<Props> = ({ onViewOrder, refresh }) => {
  const [orders, setOrders] = useState<PrintOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [guideOpen, setGuideOpen] = useState(true)

  const toggleGuide = () => setGuideOpen(!guideOpen)

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true)
      const response = await BadgeRequestService.getOrdersForVerification({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm,
        status: statusFilter,
      })
      console.log("API Response:", response)
      setOrders(response.results)
      setTotalCount(response.count)
      setTotalPages(response.total_pages || Math.ceil(response.count / pageSize))
    } catch (error) {
      console.error("Failed to fetch orders:", error)
      toast.error("Failed to load orders")
    } finally {
      setLoading(false)
    }
  }, [currentPage, pageSize, searchTerm, statusFilter])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders, refresh])

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }, [searchTerm, statusFilter])

  // Check if order needs payment
  const needsPayment = (order: PrintOrder): boolean => {
    const totalAmount = calculateTotalAmount(order)
    return order.status === "pending" && !order.is_postpaid && totalAmount > 0
  }

  const handleQuickApprove = async (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await BadgeRequestService.approveOrder(orderId)
      toast.success("Order approved successfully!")
      fetchOrders()
    } catch (error) {
      console.error("Failed to approve order:", error)
      toast.error("Failed to approve order")
    }
  }

  const handlePageChange = (page: number) => {
    console.log("Page changed to:", page)
    setCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    console.log("Page size changed to:", newPageSize)
    setPageSize(newPageSize)
    setCurrentPage(1) // Reset to first page
  }

  // Calculate total amount from order details
  const calculateTotalAmount = (order: PrintOrder): number => {
    if (!order.order_details) return 0
    return order.order_details.reduce((total, detail) => {
      return total + Number.parseFloat(detail.amount)
    }, 0)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning"
      case "paid":
        return "info"
      case "approved":
        return "success"
      case "printed":
        return "primary"
      case "cancelled":
        return "danger"
      case "postpaid":
        return "secondary"
      default:
        return "dark"
    }
  }

  const columns = [
    {
      name: "Order #",
      selector: (row: PrintOrder) => row.order_number,
      sortable: true,
      width: "180px",
      cell: (row: PrintOrder) => {
        const paymentRequired = needsPayment(row)
        return (
          <div className="position-relative">
            <strong className={paymentRequired ? "text-danger" : "text-primary"}>{row.order_number}</strong>
            {row.is_postpaid && (
              <div>
                <Badge color="info" size="sm">
                  Postpaid
                </Badge>
              </div>
            )}
            {paymentRequired && (
              <div className="position-absolute top-0 end-0" style={{ transform: "translate(50%, -50%)" }}>
                <FaExclamationTriangle className="text-danger" size={12} title="Payment Required" />
              </div>
            )}
          </div>
        )
      },
    },
    {
      name: "Exhibitor",
      selector: (row: PrintOrder) => row.participation_details?.exhibitor_name || "",
      sortable: true,
      width: "220px",
    },
    {
      name: "Exhibition",
      selector: (row: PrintOrder) => row.participation_details?.exhibition_title || "",
      sortable: true,
      width: "200px",
    },
    {
      name: "Amount",
      selector: (row: PrintOrder) => calculateTotalAmount(row),
      sortable: true,
      width: "120px",
      cell: (row: PrintOrder) => {
        const totalAmount = calculateTotalAmount(row)
        const paymentRequired = needsPayment(row)
        return (
          <div>
            <strong className={paymentRequired ? "text-danger" : ""}>RWF {totalAmount.toLocaleString()}</strong>
            {totalAmount === 0 && (
              <div>
                <Badge color="success" size="sm">
                  FREE
                </Badge>
              </div>
            )}
            {paymentRequired && (
              <div>
                <Badge color="danger" size="sm">
                  <FaCreditCard size={8} className="me-1" />
                  UNPAID
                </Badge>
              </div>
            )}
          </div>
        )
      },
    },
    {
      name: "Status",
      selector: (row: PrintOrder) => row.status,
      sortable: true,
      width: "100px",
      cell: (row: PrintOrder) => (
        <Badge color={getStatusColor(row.status)} pill>
          {row.status.toUpperCase()}
        </Badge>
      ),
    },
    {
      name: "Created",
      selector: (row: PrintOrder) => row.created_at,
      sortable: true,
      width: "100px",
      cell: (row: PrintOrder) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      name: "Actions",
      width: "80px",
      cell: (row: PrintOrder) => {
        const paymentRequired = needsPayment(row)
        return (
          <div className="d-flex gap-1">
            <Button color="primary" size="xs" onClick={() => onViewOrder(row)} title="View Details">
              <FaEye size={10} />
            </Button>
            {/* Only show approve button if payment is not required or if order is already paid */}
            {!paymentRequired && (row.status === "paid" || row.status === "pending") && (
              <Button color="success" size="xs" onClick={(e) => handleQuickApprove(row.id, e)} title="Quick Approve">
                <FaCheck size={10} />
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
      },
    },
    rows: {
      style: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      },
    },
  }

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
        </PaginationItem>,
      )
    }
    return items
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h5 className="mb-0">Orders for Verification</h5>
              <Button color="link" className="p-0 ms-2" onClick={toggleGuide} title="Show Verification Guidelines">
                <FaInfoCircle size={16} />
              </Button>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Badge color="info" pill>
                {totalCount} total orders
              </Badge>
              <Badge color="secondary" pill>
                Page {currentPage} of {totalPages}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Filters */}
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label>
                  <FaSearch className="me-2" />
                  Search
                </Label>
                <div className="position-relative">
                  <Input
                    type="text"
                    placeholder="Search by order number, exhibitor, exhibition, or person name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ps-5"
                  />
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                </div>
                <small className="text-muted">
                  <FaUser className="me-1" size={12} />
                  You can search by staff member's first name or last name
                </small>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Status</Label>
                <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="approved">Approved</option>
                  <option value="postpaid">Postpaid</option>
                  <option value="cancelled">Cancelled</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Page Size</Label>
                <Input type="select" value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          {/* Search Info */}
          {searchTerm && (
            <div className="mb-3">
              <Badge color="info" className="me-2">
                <FaSearch className="me-1" size={12} />
                Searching: "{searchTerm}"
              </Badge>
              <small className="text-muted">
                Results include matches in order numbers, exhibitors, exhibitions, and staff member names
              </small>
            </div>
          )}
          {/* DataTable */}
          <DataTable
            columns={columns}
            data={orders}
            pagination={false} // Disable built-in pagination
            progressPending={loading}
            progressComponent={
              <div className="d-flex justify-content-center align-items-center py-4">
                <Spinner color="primary" />
              </div>
            }
            noDataComponent={
              <div className="text-center py-4">
                <div className="text-muted">
                  {searchTerm || statusFilter !== "all" ? "No orders match your filters" : "No orders found"}
                </div>
                {searchTerm && (
                  <small className="text-muted d-block mt-2">
                    Try searching with different keywords or check spelling
                  </small>
                )}
              </div>
            }
            customStyles={customStyles}
            onRowClicked={onViewOrder}
            highlightOnHover
            pointerOnHover
          />
          {/* Custom Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of{" "}
                {totalCount} entries
              </div>
              <Pagination>
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink onClick={() => handlePageChange(currentPage - 1)}>
                    <FaChevronLeft />
                  </PaginationLink>
                </PaginationItem>
                {currentPage > 3 && (
                  <>
                    <PaginationItem>
                      <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                    </PaginationItem>
                    {currentPage > 4 && (
                      <PaginationItem disabled>
                        <PaginationLink>...</PaginationLink>
                      </PaginationItem>
                    )}
                  </>
                )}
                {renderPaginationItems()}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <PaginationItem disabled>
                        <PaginationLink>...</PaginationLink>
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationLink onClick={() => handlePageChange(totalPages)}>{totalPages}</PaginationLink>
                    </PaginationItem>
                  </>
                )}
                <PaginationItem disabled={currentPage === totalPages}>
                  <PaginationLink onClick={() => handlePageChange(currentPage + 1)}>
                    <FaChevronRight />
                  </PaginationLink>
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Verification Guide Modal */}
      <VerificationGuideModal isOpen={guideOpen} toggle={toggleGuide} />
    </>
  )
}

export default OrderVerificationList
